import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ShieldPlus, UserMinus } from 'lucide-react';
import { useAuthState } from '../hooks/useAuth';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useAdmins } from '../hooks/useAdminData';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { Input, Label } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';
import {
  promoteToAdmin,
  revokeAdmin,
  translateCallableError,
} from '../lib/adminCallables';
import { validateInvitationForm } from '../lib/invitationForm';

export function AdminPage() {
  const { user } = useAuthState();
  const { isAdmin, loading: claimLoading } = useIsAdmin();
  const { data: admins, loading: adminsLoading } = useAdmins();

  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  if (claimLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const emailValidation = validateInvitationForm(email, 'editor');
  const emailOk =
    emailValidation.errors.filter((e) => e !== 'role-invalid').length === 0;

  const onAdd = async () => {
    if (!emailOk) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await promoteToAdmin(emailValidation.email);
      setMessage('管理者に追加しました。');
      setEmail('');
      setAdding(false);
    } catch (e) {
      setError(translateCallableError(e).message);
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (uid: string) => {
    setError(null);
    setMessage(null);
    try {
      await revokeAdmin(uid);
      setMessage('管理者権限を取り消しました。');
    } catch (e) {
      setError(translateCallableError(e).message);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <header className="flex items-center gap-3">
        <Link to="/" className="text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <h1 className="text-2xl font-semibold">管理者</h1>
      </header>
      <p className="mt-2 text-sm text-slate-600">
        あなたのアカウントは管理者として登録されています。
      </p>

      <div className="mt-6 flex justify-end">
        <Button onClick={() => setAdding(true)}>
          <ShieldPlus className="mr-1 h-4 w-4" /> 管理者を追加
        </Button>
      </div>

      {message ? (
        <p className="mt-3 text-xs text-green-700">{message}</p>
      ) : null}
      {error ? <p className="mt-3 text-xs text-red-600">{error}</p> : null}

      <section className="card mt-4 p-0">
        {adminsLoading ? (
          <div className="p-6">
            <Spinner />
          </div>
        ) : admins.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500">
            管理者はいません。
          </div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {admins.map((a) => (
              <li
                key={a.uid}
                className="flex items-center justify-between px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {a.displayName || a.email}
                  </div>
                  <div className="truncate text-xs text-slate-500">
                    {a.email}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    UID: {a.uid}
                    {a.grantedByUserId
                      ? ` · 付与者 ${a.grantedByUserId}`
                      : ' · bootstrap'}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  className="!px-2"
                  aria-label="権限を外す"
                  disabled={a.uid === user?.uid}
                  onClick={() => onRevoke(a.uid)}
                >
                  <UserMinus className="h-4 w-4 text-red-500" />
                  <span className="ml-1 text-xs">権限を外す</span>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog
        open={adding}
        onClose={() => setAdding(false)}
        title="管理者を追加"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAdding(false)}>
              キャンセル
            </Button>
            <Button onClick={onAdd} disabled={!emailOk || busy}>
              追加
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            対象ユーザーは事前に一度サインインしている必要があります。
          </p>
          <div>
            <Label htmlFor="admin-email">メールアドレス</Label>
            <Input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
            />
          </div>
          {error ? <p className="text-xs text-red-600">{error}</p> : null}
        </div>
      </Dialog>
    </div>
  );
}
